import type { TFunction } from "i18next";

/**
 * A `/api/service-categories` row as far as a label needs it. Exported because
 * every caller has to keep the whole row: `name` alone is the default
 * language's column, and a screen that stores only that renders one fixed
 * language whatever the UI is set to (BUG-34).
 */
export type LocalizableCategory = {
  key?: string;
  id?: string;
  name?: string;
  nameEt?: string | null;
  nameEn?: string | null;
  nameRu?: string | null;
};

const FIELD_BY_LANG: Record<string, "nameEt" | "nameEn" | "nameRu"> = {
  et: "nameEt",
  en: "nameEn",
  ru: "nameRu",
};

/**
 * Resolve a localized category name with a safe fallback chain:
 *   DB column for the active language
 *   → i18n key `serviceCategories.<key>` (legacy hardcoded labels)
 *   → DB `name` (default language)
 *   → key/id.
 *
 * Pure function (takes `t`) so it can be called inside render loops and
 * non-component helpers. `language` is the raw i18n value (e.g. "et-EE").
 */
export function getCategoryName(
  category: LocalizableCategory,
  language: string,
  t: TFunction
): string {
  const lang = (language || "et").split("-")[0];
  const field = FIELD_BY_LANG[lang];

  const localized = field ? category[field] : null;
  if (localized) return localized;

  const key = category.key ?? category.id;
  if (key) {
    const fromI18n = t(`serviceCategories.${key}`, { defaultValue: "" });
    if (fromI18n) return fromI18n;
  }

  return category.name || key || "";
}
